import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Box, Typography } from '@mui/material'
import { CommonSlider } from '@/components/common'
import {
  ChipSelector,
  ConfirmAppointmentButton,
  DonorSearchModal,
  AppointmentConfirmedDialog,
} from '@/components/donation'
import {
  useGetAppointmentAvailability,
  useSearchDonors,
  useScheduleDonor,
  useChairpersonScheduleGuest,
} from '@/hooks'
import {
  timeToMinutes,
  formatUtcDate,
  formatDisplayTime,
  formatTenantCalendarDate,
} from '@/utils'
import { ScheduleDonationSkeleton } from '@/pages/schedule-donation/Skeleton'

const OPERATION_TYPE = 'drives'

export const ScheduleDriveView = ({
  driveId,
  driveDate,
  selectedAccountId,
  onlineBookingAllowed,
  marketingStartDate,
  loadingDriveInfo = false,
}) => {
  const {
    appointmentAvailability,
    getAppointmentAvailability,
    loadingAppointmentAvailability,
  } = useGetAppointmentAvailability()
  const { searchDonors, loadingSearchDonors } = useSearchDonors()
  const { scheduleDonor, loadingScheduleDonor } = useScheduleDonor()
  const { scheduleGuest, loadingScheduleGuest } = useChairpersonScheduleGuest()

  const [timeRange, setTimeRange] = useState(['8:00 AM', '10:00 AM'])
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [donorSearchOpen, setDonorSearchOpen] = useState(false)
  const [successDialogOpen, setSuccessDialogOpen] = useState(false)
  const [successData, setSuccessData] = useState({
    name: '',
    date: '',
    time: '',
  })

  useEffect(() => {
    if (!driveId) return
    getAppointmentAvailability({
      operation_id: driveId,
      operation_type: OPERATION_TYPE,
      procedure_type_id: null,
    })
  }, [driveId])

  useEffect(() => {
    if (!appointmentAvailability || appointmentAvailability.length === 0) return

    const availableEntries = appointmentAvailability.filter(entry =>
      Array.isArray(entry?.procedure_types)
        ? entry.procedure_types.some(pt =>
            Array.isArray(pt?.slots)
              ? pt.slots.some(slot => !!slot?.is_available)
              : false
          )
        : false
    )

    if (!availableEntries.length) return

    const earliestAvail = availableEntries.reduce((min, a) => {
      const minMinutes = timeToMinutes(formatUtcDate(min.start_time, 'h:mm a'))
      const aMinutes = timeToMinutes(formatUtcDate(a.start_time, 'h:mm a'))
      return aMinutes < minMinutes ? a : min
    })
    const latestAvail = availableEntries.reduce((max, a) => {
      const maxMinutes = timeToMinutes(formatUtcDate(max.start_time, 'h:mm a'))
      const aMinutes = timeToMinutes(formatUtcDate(a.start_time, 'h:mm a'))
      return aMinutes > maxMinutes ? a : max
    })

    setTimeRange([
      formatUtcDate(earliestAvail.start_time, 'hh:mm a'),
      formatUtcDate(latestAvail.start_time, 'hh:mm a'),
    ])
  }, [appointmentAvailability])

  const availableAppointments = useMemo(() => {
    if (!appointmentAvailability || appointmentAvailability.length === 0)
      return []

    const [rangeStart, rangeEnd] = timeRange
    const rangeStartMinutes = timeToMinutes(rangeStart)
    const rangeEndMinutes = timeToMinutes(rangeEnd)

    return appointmentAvailability
      .map((appt, index) => ({
        startTime: appt.start_time,
        time: formatUtcDate(appt.start_time, 'h:mm a'),
        procedureTypes: appt?.procedure_types.map(pt => ({
          id: `appt-chip-${pt.procedure_type_id}-${index}`,
          availableSlots: pt.available_slots,
          totalSlots: pt.total_slots,
          occupiedSlots: pt.occupied_slots,
          disabled: pt.available_slots === 0,
          text1: pt.procedure_name,
          text2:
            pt.available_slots === 0
              ? 'Full'
              : `${pt.available_slots} Opening${pt.available_slots !== 1 ? 's' : ''}`,
          ...pt,
        })),
      }))
      .filter(appt => {
        const slotMinutes = timeToMinutes(
          formatUtcDate(appt.startTime, 'h:mm a')
        )
        if (rangeEndMinutes < rangeStartMinutes) {
          return (
            slotMinutes >= rangeStartMinutes || slotMinutes <= rangeEndMinutes
          )
        }
        return (
          slotMinutes >= rangeStartMinutes && slotMinutes <= rangeEndMinutes
        )
      })
      .sort((a, b) => {
        const timeA = timeToMinutes(formatUtcDate(a.startTime, 'h:mm a'))
        const timeB = timeToMinutes(formatUtcDate(b.startTime, 'h:mm a'))
        if (rangeEndMinutes < rangeStartMinutes) {
          const aAfterMidnight = timeA <= rangeEndMinutes
          const bAfterMidnight = timeB <= rangeEndMinutes
          if (aAfterMidnight && !bAfterMidnight) return -1
          if (!aAfterMidnight && bAfterMidnight) return 1
        }
        return timeA - timeB
      })
  }, [appointmentAvailability, timeRange])

  const isBookingUnavailable =
    !loadingDriveInfo && onlineBookingAllowed === false

  const emptyMessage = useMemo(() => {
    if (onlineBookingAllowed === false) {
      const todayIso = formatUtcDate(new Date(), 'yyyy-MM-dd')
      if (
        marketingStartDate &&
        /^\d{4}-\d{2}-\d{2}$/.test(marketingStartDate) &&
        marketingStartDate > todayIso
      ) {
        return `Appointments are not available yet. Online scheduling opens on ${formatTenantCalendarDate(
          marketingStartDate,
          'MM/dd/yyyy'
        )}.`
      }
      return 'Appointments are not available for this drive.'
    }
    return 'No appointments available'
  }, [onlineBookingAllowed, marketingStartDate])

  const confirmedSlotData = useMemo(() => {
    if (!selectedSlot?.slots) return null
    return selectedSlot.slots.find(slot => slot.is_available)
  }, [selectedSlot])

  const slotId = confirmedSlotData?.slot_id ?? null
  const slotTime = confirmedSlotData?.start_time
    ? formatDisplayTime(confirmedSlotData.start_time, 'h:mm a')
    : ''

  const fetchAvailability = () => {
    if (driveId) {
      getAppointmentAvailability({
        operation_id: driveId,
        operation_type: OPERATION_TYPE,
        procedure_type_id: null,
      })
    }
  }

  const handleConfirmAppointmentClick = () => {
    if (selectedSlot && confirmedSlotData) setDonorSearchOpen(true)
  }

  const handleDonorConfirmSuccess = ({ name, time }) => {
    setSuccessData({
      name: name || '',
      date: driveDate || '',
      time: time || slotTime,
    })
    setSuccessDialogOpen(true)
    setSelectedSlot(null)
    fetchAvailability()
  }

  return (
    <>
      {!isBookingUnavailable ? (
        <Box sx={{ px: { xs: 2, md: 2 }, mb: { xs: 1, md: 0 } }}>
          <CommonSlider
            id='appointment-time-range'
            variant='time'
            isRange={true}
            value={timeRange}
            onChange={setTimeRange}
            step={15}
          />
        </Box>
      ) : null}

      {!loadingAppointmentAvailability &&
        !loadingDriveInfo &&
        !availableAppointments.length && (
          <Typography
            gutterBottom
            sx={{
              mt: { xs: 3, md: 5 },
              textAlign: 'center',
              typography: { xs: 'body2', md: 'h6' },
              px: 2,
            }}
          >
            {emptyMessage}
          </Typography>
        )}

      {loadingAppointmentAvailability || loadingDriveInfo ? (
        <ScheduleDonationSkeleton />
      ) : (
        <Box
          sx={{
            mt: { xs: 2, md: 4 },
            px: { xs: 1, md: 2 },
            overflowY: { xs: 'hidden', md: 'auto' },
            maxHeight: { xs: '100%', md: '500px' },
          }}
        >
          {availableAppointments.map((appt, index) => (
            <Box
              key={`${appt.startTime}-${index}`}
              sx={{ mb: { xs: 1.5, md: 2 } }}
            >
              <ChipSelector
                title={appt.time}
                id={`appointment-appt-${index}`}
                chips={appt.procedureTypes}
                returnObject
                value={selectedSlot}
                onChange={setSelectedSlot}
              />
            </Box>
          ))}
        </Box>
      )}

      <ConfirmAppointmentButton
        selectedSlot={selectedSlot}
        loading={false}
        onClick={handleConfirmAppointmentClick}
        hasAppointments={!!availableAppointments.length}
      />

      <DonorSearchModal
        open={donorSearchOpen}
        onClose={() => setDonorSearchOpen(false)}
        slotId={slotId}
        slotTime={slotTime}
        onConfirmSuccess={handleDonorConfirmSuccess}
        searchDonors={(query, page, limit) =>
          searchDonors(query, page, limit, selectedAccountId)
        }
        scheduleDonor={scheduleDonor}
        loadingSearch={loadingSearchDonors}
        loadingConfirm={loadingScheduleDonor}
        scheduleGuest={scheduleGuest}
        loadingScheduleGuest={loadingScheduleGuest}
      />

      <AppointmentConfirmedDialog
        open={successDialogOpen}
        name={successData.name}
        date={successData.date}
        time={successData.time}
        scheduledByChairperson
        onClose={() => setSuccessDialogOpen(false)}
      />
    </>
  )
}

ScheduleDriveView.propTypes = {
  driveId: PropTypes.string,
  driveDate: PropTypes.string,
  selectedAccountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onlineBookingAllowed: PropTypes.bool,
  marketingStartDate: PropTypes.string,
  loadingDriveInfo: PropTypes.bool,
}
